import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { OrderService } from '../../order.service';
import { IOrder } from '../../../model/interfaces/order';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicField, DynamicFieldType, DynamicFieldTypes, IDatepickerOptions, InputTypes } from '@mt-ng2/dynamic-form';
import { PickupService } from '../../../model/shared-entities/pickups/pickup.service';
import { NotificationsService } from '@mt-ng2/notifications-module';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ISelectionChangedEvent } from '@mt-ng2/multiselect-control';
import { IPickup } from '../../../model/interfaces/pickup';
import { CommonFunctions } from '../../../common/services/common-functions.service';
import { CarrierService } from '../../../carriers/services/carrier.service';
import { finalize, forkJoin, Observable } from 'rxjs';
import { ICarrier } from '../../../model/interfaces/carrier';
import { DriverTimeSlot } from '../../../model/classes/driver-time-slot';
import { PhoneFormatPipe } from '../../../common/pipes/phone-format';
import { CommonService } from '../../../common/services/common.service';
import { PickupStatus } from '../../order-basic-info/pickup-status-enum';
import { IPickupParams } from '../../../model/interfaces/custom/pickup-params';
import { ISelectionChangedEvent as ITypeAheadSelectionChangedEvent, VirtualTypeAheadGetItemsFunction } from '@mt-ng2/type-ahead-control';
import { IEntitySearchParams } from '@mt-ng2/common-classes';
import { IDriverDTO } from '../../../model/interfaces/custom/driver-dto';

@Component({
    selector: 'app-pickup-scheduling-time-selection',
    templateUrl: './time-selection.component.html',
    styleUrls: ['./time-selection.component.css'],
    standalone: false
})
export class PickupSchedulingTimeSelectionComponent implements OnInit {
    selectedPickup: IPickup | null = null;
    selectedOrders: IOrder[] = [];
    drivers: IDriverDTO[] = [];
    selectableDrivers: IDriverDTO[] = [];
    carriers: ICarrier[] = [];
    driverSelectionForm!: FormGroup;
    selectedDriverTouched = false;
    selectedCarrierTouched = false;
    selectedDriver: IDriverDTO | null = null;
    selectedCarrier: ICarrier | null = null;
    selectedDateTime = '';
    selectedDateTimeCopy = '';
    selectedDriverIdCopy = 0;
    pickupStatusIdCopy = 0;
    availableDateTimes: DriverTimeSlot[] = [];
    startDate = new Date();
    endDate = new Date();
    today = new Date();
    phoneFormatPipe = new PhoneFormatPipe();
    pickupStatus = PickupStatus;
    confirmButtonWasClicked = false;
    dateRequestedField!: DynamicField;
    isLoadingAvailableTimes = false;
    hasLoadedAvailableTimes = false;
    private availableTimesRequestId = 0;
    getItems: VirtualTypeAheadGetItemsFunction = this.getDrivers.bind(this);
    datepickerOptions: IDatepickerOptions = {
        minDate: {
            year: this.today.getFullYear(),
            month: this.today.getMonth() + 1,
            day: this.today.getDate(),
        },
    };

    constructor(
        private orderService: OrderService,
        private pickupService: PickupService,
        private notificationsService: NotificationsService,
        private router: Router,
        private fb: FormBuilder,
        private carrierService: CarrierService,
        private commonService: CommonService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.orderService.selectedPickupOrders$.subscribe((selectedOrders) => {
            if (!selectedOrders || selectedOrders.length <= 0) {
                void this.router.navigate([`/create-requests/order-selection`]);
                return;
            }

            this.selectedOrders = selectedOrders;
        });

        forkJoin([this.carrierService.getActive()]).subscribe(([carriers]) => {
            this.carriers = carriers ?? [];
            this.carriers.sort((a, b) => a.Name.localeCompare(b.Name));
            this.pickupService.selectedPickup$.subscribe((selectedPickup) => {
                if (selectedPickup) {
                    this.selectedPickup = selectedPickup;
                    this.selectedCarrier = this.carriers.find((f) => f.Id === selectedPickup.CarrierId) ?? null;
                    const dateTime = this.selectedPickup.DateConfirmed ?? this.selectedPickup.DateRequested ?? this.today;
                    this.selectedDateTime = dateTime.toString();
                    this.selectedDateTimeCopy = this.selectedDateTime;
                    this.selectedDriverIdCopy = this.selectedPickup.DriverId ?? 0;
                    this.pickupStatusIdCopy = this.selectedPickup.StatusId;
                    this.startDate = new Date(dateTime);
                    this.endDate = new Date(dateTime);
                    const driverId = this.selectedPickup.DriverId ?? 0;
                    if (driverId > 0) {
                        this.pickupService.getPickupDriverById(driverId).subscribe((driver) => {
                            this.selectedDriver = driver;
                        });
                    }
                }

                const rawValue = this.selectedPickup?.DateConfirmed ?? this.selectedPickup?.DateRequested ?? this.today;
                const value = rawValue instanceof Date ? rawValue : new Date(rawValue);
                this.dateRequestedField = new DynamicField({
                    formGroup: '',
                    label: '',
                    labelHtml: `<i class="fa fa-lg fa-fw fa-calendar"></i> <b>Select Date</b>:`,
                    name: 'dateRequested',
                    type: new DynamicFieldType({
                        fieldType: DynamicFieldTypes.Input,
                        inputType: InputTypes.Datepicker,
                        datepickerOptions: this.datepickerOptions,
                    }),
                    validators: { required: true },
                    value: value,
                });

                this.driverSelectionForm = this.fb.group({
                    deliveryZipCode: [this.selectedPickup?.DeliveryZipCode ?? '', [Validators.pattern('^[0-9]{5}(?:-[0-9]{4})?$')]],
                    dateRequested: new FormControl(this.dateRequestedField.value, [Validators.required]),
                });
            });
        });
    }

    getDrivers(searchText: string): Observable<IDriverDTO[]> {
        const searchEntity: IEntitySearchParams = {
            query: searchText,
        };
        return this.pickupService.getPickupDriversByName(searchEntity);
    }

    onDriverSelected(event: ITypeAheadSelectionChangedEvent): void {
        this.selectedDriverTouched = true;
        this.selectedDriver = event.selection;
        this.getSearchTimes();
    }

    onCarrierSelected(event: ISelectionChangedEvent<ICarrier>): void {
        this.selectedCarrierTouched = true;
        const selection = event.selectedItems?.[0] ?? (event as unknown as { selection?: ICarrier }).selection ?? null;
        this.selectedCarrier = this.carriers.find((f) => f.Id === selection?.Id) ?? null;
        this.selectedDriver = null;
        this.getSearchTimes();
    }

    startDateChange(event: Date): void {
        this.startDate = event;
        this.endDate = event;
        setTimeout(() => this.getSearchTimes());
    }

    onDriverSelectionFormSubmit(): void {
        const selectedDateTimeUtc = CommonFunctions.getUTCEquivalent(this.selectedDateTime);
        if (!this.driverSelectionForm || !this.selectedCarrier || !selectedDateTimeUtc) {
            this.selectedCarrierTouched = true;
            return;
        }

        if ((this.selectedPickup?.Id ?? 0) > 0) {
            const selectedPickup = this.selectedPickup;
            if (!selectedPickup) return;

            selectedPickup.Orders = this.selectedOrders;
            selectedPickup.DriverId = this.selectedDriver?.Id;
            selectedPickup.DeliveryZipCode = this.driverSelectionForm.controls.deliveryZipCode.value as string;
            selectedPickup.CarrierId = this.selectedCarrier.Id;

            let successMessage = 'Your pickup appointment has been updated.';
            if (this.confirmButtonWasClicked) {
                selectedPickup.StatusId = PickupStatus.Confirmed;
                successMessage = 'Your pickup appointment has been confirmed.';
            }

            if (selectedPickup.StatusId === PickupStatus.Confirmed) {
                selectedPickup.DateConfirmed = selectedDateTimeUtc;
            } else {
                selectedPickup.DateRequested = selectedDateTimeUtc;
            }

            selectedPickup.Driver = undefined;
            this.pickupService.updateWithFks(selectedPickup).subscribe({
                next: () => {
                    this.sendPickupEmails(selectedPickup.Id, successMessage);
                },
                error: (errorResponse: HttpErrorResponse) => {
                    this.handleHttpErrorResponse(errorResponse);
                },
            });

            return;
        }

        const params: IPickupParams = {
            dateRequested: selectedDateTimeUtc,
            dateConfirmed: null,
            orders: this.selectedOrders,
            carrierId: this.selectedCarrier.Id,
            driverId: this.selectedDriver?.Id ?? 0,
            deliveryZipCode: this.driverSelectionForm.controls.deliveryZipCode.value as string,
            statusId: null,
        };

        let successMessage = 'Your pickup appointment has been updated.';
        if (this.confirmButtonWasClicked) {
            successMessage = 'Your pickup appointment has been confirmed.';
            params.statusId = PickupStatus.Confirmed;
            params.dateConfirmed = selectedDateTimeUtc;
        }

        this.pickupService.createScheduledPickup(params).subscribe({
            next: (pickup: IPickup) => {
                // If confirming a new pickup, check for credit processing only if driver is assigned
                if (this.confirmButtonWasClicked && pickup.DriverId && pickup.DriverId > 0) {
                    this.pickupService.checkCarrierCreditEligibility(pickup.Id).subscribe({
                        next: (creditEligibility) => {
                            this.pickupService.getRemainingBalance(pickup.Id).subscribe({
                                next: (remainingBalance) => {
                                    if (creditEligibility.canUseCredit && creditEligibility.availableCredit >= remainingBalance) {
                                        this.pickupService.processPickupWithCredit(pickup.Id).subscribe({
                                            next: (result) => {
                                                if (result.Success) {
                                                    this.notificationsService.success('Payment processed successfully using carrier credit.');
                                                }
                                            },
                                            error: (errorResponse: HttpErrorResponse) => {
                                                this.handleHttpErrorResponse(errorResponse);
                                            },
                                        });
                                    }
                                },
                                error: (errorResponse: HttpErrorResponse) => {
                                    this.handleHttpErrorResponse(errorResponse);
                                },
                            });
                        },
                    });
                }

                this.sendPickupEmails(pickup.Id, successMessage);
            },
            error: (errorResponse: HttpErrorResponse) => {
                this.handleHttpErrorResponse(errorResponse);
            },
        });
    }

    onDateTimeSelected(dateTime: DriverTimeSlot): void {
        if (this.isSlotFull(dateTime)) return;

        this.selectedDateTime = dateTime.TimeSlot?.toString() ?? '';
    }

    isSlotFull(slot: DriverTimeSlot): boolean {
        return !slot || slot.NumberOfSlots <= 0;
    }

    showNoAvailableTimesMessage(): boolean {
        return this.hasLoadedAvailableTimes && !this.isLoadingAvailableTimes && this.availableDateTimes.length === 0;
    }

    pickupFormIsInvalid(): boolean {
        if (!this.selectedCarrier || !this.selectedDateTime || this.driverSelectionForm.controls.deliveryZipCode.invalid) return true;

        return !this.availableDateTimes.find((time) => time.TimeSlot.toString() === this.selectedDateTime && !this.isSlotFull(time));
    }

    pickupHasNotBeenAltered(): boolean {
        if (!this.selectedPickup) return false;

        const zipCode = this.driverSelectionForm.controls.deliveryZipCode.value as string;
        const carrierId = this.selectedCarrier?.Id ?? null;
        const driverId = this.selectedDriver?.Id ?? null;
        const originalOrderIds = (this.selectedPickup.Orders ?? []).map((o) => o.Id);
        const selectedOrderIds = this.selectedOrders.map((o) => o.Id);

        return (
            this.selectedPickup.DeliveryZipCode === zipCode &&
            this.selectedPickup.CarrierId === carrierId &&
            this.selectedPickup.DriverId === driverId &&
            this.selectedDateTimeCopy === this.selectedDateTime &&
            CommonFunctions.areArraysIdentical(originalOrderIds, selectedOrderIds)
        );
    }

    cancelPickup(pickup: IPickup): void {
        this.commonService.cancelPickup(pickup);
    }

    confirmPickup(): void {
        this.confirmButtonWasClicked = true;
        this.onDriverSelectionFormSubmit();
    }

    totalPickupPrePayment(pickup: IPickup): number {
        if (!pickup.PickupPaymentDetails) return 0;

        return pickup.PickupPaymentDetails.filter((d) => d.IsSuccessful).reduce((sum, cur) => sum + cur.Amount, 0);
    }

    totalRemainingBalance(pickup: IPickup): number {
        const remainingBalance = pickup.SecurityGateFee - this.totalPickupPrePayment(pickup);
        return remainingBalance > 0 ? remainingBalance : 0;
    }

    private getSearchTimes() {
        if (!this.selectedOrders.length) return;

        const requestId = ++this.availableTimesRequestId;

        this.isLoadingAvailableTimes = true;
        this.hasLoadedAvailableTimes = false;
        this.availableDateTimes = [];
        this.selectedDateTime = '';
        this.cdr.detectChanges();

        this.pickupService
            .getDriverSearchTimes(
                this.selectedOrders.map((o) => o.Id),
                CommonFunctions.setToMidnight(this.startDate),
                CommonFunctions.setToMidnight(this.endDate),
                this.selectedOrders[0].CustomerId,
                this.selectedOrders[0].Warehouse?.OfficeId ?? 0,
                this.selectedDriver?.Id ?? 0,
            )
            .pipe(
                finalize(() => {
                    if (requestId !== this.availableTimesRequestId) return;

                    this.isLoadingAvailableTimes = false;
                    this.hasLoadedAvailableTimes = true;
                }),
            )
            .subscribe((availableDateTimes) => {
                if (requestId !== this.availableTimesRequestId) return;

                this.availableDateTimes = availableDateTimes;
                const selectedSlot = this.availableDateTimes.find((time) => time.TimeSlot.toString() === this.selectedDateTime);
                if (selectedSlot && this.isSlotFull(selectedSlot)) {
                    this.selectedDateTime = '';
                }
            });
    }

    private sendPickupEmails(pickupId: number, successMessage: string): void {
        if (this.confirmButtonWasClicked && this.selectedDriver) {
            this.pickupService.sendConfirmedNotifications(pickupId).subscribe({
                next: () => {
                    // Check for credit eligibility after confirming the pickup
                    this.pickupService.checkCarrierCreditEligibility(pickupId).subscribe({
                        next: (creditEligibility) => {
                            this.pickupService.getRemainingBalance(pickupId).subscribe({
                                next: (remainingBalance) => {
                                    if (creditEligibility.canUseCredit && creditEligibility.availableCredit >= remainingBalance) {
                                        this.pickupService.processPickupWithCredit(pickupId).subscribe({
                                            next: (result) => {
                                                if (result.Success) {
                                                    this.notificationsService.success('Payment processed successfully using carrier credit.');
                                                }
                                            },
                                            error: (errorResponse: HttpErrorResponse) => {
                                                this.handleHttpErrorResponse(errorResponse);
                                            },
                                        });
                                    }
                                },
                                error: (errorResponse: HttpErrorResponse) => {
                                    this.handleHttpErrorResponse(errorResponse);
                                },
                            });
                        },
                    });

                    this.showSuccessModal(successMessage);
                },
                error: (errorResponse: HttpErrorResponse) => {
                    this.handleHttpErrorResponse(errorResponse);
                },
            });
            return;
        }

        this.pickupService.sendUpdatedEmails(pickupId).subscribe({
            next: () => {
                this.showSuccessModal(successMessage);
            },
            error: (errorResponse: HttpErrorResponse) => {
                this.handleHttpErrorResponse(errorResponse);
            },
        });
    }

    private showSuccessModal(successMessage: string): void {
        this.notificationsService.success(successMessage);
        void this.router.navigate(['create-requests/manage-pickups']);
    }

    private handleHttpErrorResponse(errorResponse: HttpErrorResponse): void {
        if (typeof errorResponse.error === 'string') {
            this.notificationsService.error(errorResponse.error);
        } else {
            this.notificationsService.error(errorResponse.error['ModelState'][0] as string);
        }

        if (this.selectedPickup) {
            this.selectedPickup.StatusId = this.pickupStatusIdCopy;
            this.selectedPickup.DriverId = this.selectedDriverIdCopy;
        }

        this.confirmButtonWasClicked = false;
    }
}
